import React, { useState, Fragment, FormEvent } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CandidateEducation } from '../../../Candidate/Types/CandidateType';

interface Props {
  items: CandidateEducation[];
  onChange: (items: CandidateEducation[]) => void;
  maxItems?: number;
}

const emptyItem: CandidateEducation = {
  id: '',
  institution: '',
  degree: '',
  fieldOfStudy: '',
  startYear: null,
  endYear: null,
  createdAt: '',
  updatedAt: '',
};

export const EducationManager: React.FC<Props> = ({ items, onChange, maxItems = 5 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form, setForm] = useState<CandidateEducation>(emptyItem);

  const currentList = Array.isArray(items) ? items : [];
  const isLimitReached = maxItems !== undefined && currentList.length >= maxItems;

  const closeModal = () => {
    setIsOpen(false);
    setEditingIndex(null);
    setForm(emptyItem);
  };

  const openAdd = () => {
    setEditingIndex(null);
    setForm(emptyItem);
    setIsOpen(true);
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setForm({ ...currentList[index] });
    setIsOpen(true);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.institution.trim() && !form.degree.trim()) return;

    const updated = [...currentList];
    if (editingIndex !== null) {
      updated[editingIndex] = { ...form };
    } else {
      updated.push({ ...form, id: form.id || crypto.randomUUID() });
    }
    onChange(updated);
    closeModal();
  };

  const removeItem = (index: number) => {
    const updated = currentList.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">
          Education {maxItems && `(${currentList.length}/${maxItems})`}
        </h3>
        {!isLimitReached && (
          <button
            type="button"
            onClick={openAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-cyan-400 rounded-lg text-xs font-bold hover:bg-slate-700 transition-colors border border-slate-700"
          >
            <PlusIcon className="w-3.5 h-3.5" /> ADD
          </button>
        )}
      </div>

      <div className="space-y-2">
        {currentList.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No education added yet.</p>
        ) : (
          currentList.map((item, index) => (
            <div
              key={item.id || index}
              className="flex items-start justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-700/60"
            >
              <div>
                <p className="text-sm font-bold text-slate-200">{item.degree || 'Degree'}</p>
                <p className="text-xs text-slate-400">{item.institution}{item.fieldOfStudy ? ` · ${item.fieldOfStudy}` : ''}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {item.startYear || 'Start'} → {item.endYear || 'End'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => openEdit(index)}
                  className="p-1.5 text-slate-400 hover:text-cyan-400 transition-colors"
                >
                  <PencilIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-1.5 text-slate-400 hover:text-rose-400 transition-colors"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={closeModal}>
          <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-2xl bg-slate-900 border border-slate-700 p-6 rounded-2xl shadow-2xl">
              <Dialog.Title className="text-lg font-black text-white mb-4">
                {editingIndex !== null ? 'Edit Education' : 'Add Education'}
              </Dialog.Title>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Institution"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.institution}
                    onChange={(e) => setForm({ ...form, institution: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Degree"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.degree}
                    onChange={(e) => setForm({ ...form, degree: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Field of study"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.fieldOfStudy}
                    onChange={(e) => setForm({ ...form, fieldOfStudy: e.target.value })}
                  />
                  <input
                    type="number"
                    placeholder="Start year"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.startYear ?? ''}
                    onChange={(e) => setForm({ ...form, startYear: e.target.value ? Number(e.target.value) : null })}
                  />
                  <input
                    type="number"
                    placeholder="End year"
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    value={form.endYear ?? ''}
                    onChange={(e) => setForm({ ...form, endYear: e.target.value ? Number(e.target.value) : null })}
                  />
                </div>

                <div className="flex gap-3 justify-end pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-bold text-slate-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-bold rounded-lg transition-colors"
                  >
                    Save
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};
